import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { setTextSize, setTextSizeHintSeen, textSizeHintSeen, textSizeState } from './prefs.js';
import { showsTextSizeHint, TEXT_SIZE_WORDS, TEXT_SIZES, textSizeHintWords, type TextSize } from './text-size-rules.js';

/** THE TEXT SIZE CONTROL IN THE HEADER (ported 2026-10-12 from the medrec console's TextSizeControl, Rod's pick C there; the store is
 *  prefs.ts, the rules text-size-rules.ts): a square tool beside the theme toggle, its glyph "Aa" and its name the step on screen ("Text
 *  size: Larger"), that opens a small box of three plain-word choices, Standard, Large and Larger. The choices are native radio buttons, so
 *  the keyboard works as everywhere: Enter or Space on the tool opens the box with the focus on the step on screen, the arrow keys move and
 *  pick (each pick shows at once, so a person can compare), Enter, Escape or a click outside closes it and the focus goes back to the tool.
 *  Each choice is a 24 px target or more.
 *
 *  THE HINT: the first time the console picks Large or Larger by itself (no pick of the person's yet; the window wide enough), one line under
 *  the tool says so ("Text size set to Larger for your screen; change it here."), with a ✕ that ends it. Once per browser: the ✕, a pick, or
 *  opening the box ends it for good.
 *
 *  Both boxes are fixed to the window under the tool, placed from its box, so nothing in the header line moves when they show; a resize
 *  closes the choices and places the hint again. */

/** The box's id, so the tool can say which box it opens (`aria-controls`); one header is on the page at a time. */
const BOX_ID = 'text-size-box';

interface TextSizeNow {
  size: TextSize;
  pick: (size: TextSize) => void;
  hint: boolean;
  dismissHint: () => void;
}

/** The header's text size: the visit's step and the person's pick. A pick is saved, put on the root at once (prefs.ts) and ends the hint,
 *  since the person has found the control. Each page draws its own header, so the state lives in prefs.ts and every header reads it. */
function useTextSize(): TextSizeNow {
  const [state, setState] = useState(textSizeState);
  const [hintSeen, setHintSeen] = useState(textSizeHintSeen);
  const dismissHint = useCallback(() => {
    setTextSizeHintSeen();
    setHintSeen(true);
  }, []);
  const pick = useCallback((size: TextSize) => {
    setTextSize(size);
    setState(textSizeState());
    setTextSizeHintSeen();
    setHintSeen(true);
  }, []);
  return { size: state.size, pick, hint: showsTextSizeHint(state, hintSeen), dismissHint };
}

export function TextSizeControl() {
  const text = useTextSize();
  const [open, setOpen] = useState(false);
  const tool = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const name = `Text size: ${TEXT_SIZE_WORDS[text.size]}`;

  const place = (): void => {
    const b = tool.current?.getBoundingClientRect();
    if (b === undefined) return;
    setAt({ top: Math.round(b.bottom + 6), right: Math.max(8, Math.round(window.innerWidth - b.right)) });
  };
  // Placed before paint whenever a box shows, and again on a resize (which closes the choices).
  useLayoutEffect(() => {
    if (open || text.hint) place();
  }, [open, text.hint, text.size]);
  useEffect(() => {
    if (!open && !text.hint) return;
    const onResize = (): void => {
      setOpen(false);
      place();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, text.hint]);
  // Open (once the box is placed and drawn): the focus on the step on screen; a click outside the box and the tool closes it.
  const drawn = open && at !== null;
  useEffect(() => {
    if (!drawn) return;
    box.current?.querySelector<HTMLInputElement>('input:checked')?.focus();
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node | null;
      if (t !== null && (box.current?.contains(t) || tool.current?.contains(t))) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [drawn]);

  const close = (): void => {
    setOpen(false);
    tool.current?.focus();
  };

  return (
    <>
      <button
        ref={tool}
        type="button"
        className={open ? 'iconbtn ts-tool on' : 'iconbtn ts-tool'}
        aria-label={name}
        title={name}
        aria-expanded={open}
        aria-controls={open ? BOX_ID : undefined}
        data-testid="text-size"
        data-size={text.size}
        onClick={() => {
          if (text.hint) text.dismissHint();
          setOpen((o) => !o);
        }}
      >
        <span aria-hidden="true" className="ts-glyph">
          A<small>a</small>
        </span>
      </button>
      {open && at !== null ? (
        <div
          ref={box}
          id={BOX_ID}
          className="ts-box"
          role="group"
          aria-label="Text size"
          data-testid="text-size-box"
          style={{ top: at.top, right: at.right }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              close();
            }
          }}
        >
          <p className="ts-head" aria-hidden="true">
            Text size
          </p>
          {TEXT_SIZES.map((s) => (
            <label key={s} className={s === text.size ? 'ts-choice on' : 'ts-choice'} data-testid={`text-size-${s}`}>
              <input type="radio" name="text-size" value={s} checked={s === text.size} onChange={() => text.pick(s)} />
              <span>{TEXT_SIZE_WORDS[s]}</span>
            </label>
          ))}
        </div>
      ) : null}
      {text.hint && !open && at !== null ? (
        <div className="ts-hint" role="status" data-testid="text-size-hint" style={{ top: at.top, right: at.right }}>
          <span>{textSizeHintWords(text.size)}</span>
          <button type="button" className="ts-hint-x" aria-label="Dismiss the text size note" title="Dismiss" data-testid="text-size-hint-dismiss" onClick={text.dismissHint}>
            {'\u{2715}'}
          </button>
        </div>
      ) : null}
    </>
  );
}
